import React, { useState, useEffect, useCallback, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import InfoIcon from "@material-ui/icons/Info";
import SignalCellularAltIcon from "@material-ui/icons/SignalCellularAlt";
import moment from "moment";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  statCard: {
    padding: theme.spacing(2),
    textAlign: "center",
    height: "100%",
  },
  statValue: {
    fontSize: "2rem",
    fontWeight: 700,
    lineHeight: 1.2,
  },
  statLabel: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  successColor: { color: "#4caf50" },
  errorColor: { color: "#f44336" },
  warningColor: { color: "#ff9800" },
  primaryColor: { color: theme.palette.primary.main },
  filterRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "center",
  },
  filterControl: {
    minWidth: 120,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  chipSuccess: {
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    fontWeight: 600,
    fontSize: "0.7rem",
  },
  chipError: {
    backgroundColor: "#ffebee",
    color: "#c62828",
    fontWeight: 600,
    fontSize: "0.7rem",
  },
  chartSection: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  payloadBox: {
    backgroundColor: theme.palette.type === "dark" ? "#1e1e1e" : "#f5f5f5",
    borderRadius: 4,
    padding: theme.spacing(1.5),
    fontFamily: "monospace",
    fontSize: "0.78rem",
    overflowX: "auto",
    maxHeight: 400,
    overflowY: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
  },
  autoRefreshBtn: {
    marginLeft: theme.spacing(1),
  },
}));

const PERIOD_OPTIONS = [
  { label: "Última hora", value: 1 },
  { label: "Últimas 6h", value: 6 },
  { label: "Últimas 24h", value: 24 },
  { label: "Últimas 48h", value: 48 },
  { label: "Última semana", value: 168 },
];

const WebhookMonitor = () => {
  const classes = useStyles();

  const [stats, setStats] = useState(null);
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [period, setPeriod] = useState(24);
  const [filterProvider, setFilterProvider] = useState("");
  const [filterDirection, setFilterDirection] = useState("");
  const [filterSuccess, setFilterSuccess] = useState("");
  const [filterEventType, setFilterEventType] = useState("");
  const [filterPhone, setFilterPhone] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const autoRefreshRef = useRef(null);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const { data } = await api.get("/webhook-audit/stats", {
        params: { hours: period },
      });
      setStats(data);
    } catch (err) {
      toast.error("Erro ao carregar estatísticas");
    } finally {
      setStatsLoading(false);
    }
  }, [period]);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (filterProvider) params.provider = filterProvider;
      if (filterDirection) params.direction = filterDirection;
      if (filterSuccess !== "") params.success = filterSuccess;
      if (filterEventType) params.eventType = filterEventType;
      if (filterPhone) params.phone = filterPhone;

      const startDate = moment().subtract(period, "hours").toISOString();
      params.startDate = startDate;

      const { data } = await api.get("/webhook-audit", { params });
      setRecords(data.records || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error("Erro ao carregar registros");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filterProvider, filterDirection, filterSuccess, filterEventType, filterPhone, period]);

  useEffect(() => {
    fetchStats();
    fetchRecords();
  }, [fetchStats, fetchRecords]);

  useEffect(() => {
    if (autoRefresh) {
      autoRefreshRef.current = setInterval(() => {
        fetchStats();
        fetchRecords();
      }, 30000);
    } else {
      clearInterval(autoRefreshRef.current);
    }
    return () => clearInterval(autoRefreshRef.current);
  }, [autoRefresh, fetchStats, fetchRecords]);

  const handleRefresh = () => {
    fetchStats();
    fetchRecords();
  };

  const handleOpenDetail = async (record) => {
    try {
      const { data } = await api.get(`/webhook-audit/${record.id}`);
      setSelectedRecord(data);
      setDetailOpen(true);
    } catch {
      toast.error("Erro ao carregar detalhe");
    }
  };

  const formatHour = (hourStr) => moment(hourStr).format("HH:mm");

  const chartData = stats?.byHour?.map((h) => ({
    hora: formatHour(h.hour),
    Recebidos: parseInt(h.count),
    Sucesso: parseInt(h.successes),
    Falhas: parseInt(h.failures),
  })) || [];

  return (
    <MainContainer>
      <MainHeader>
        <Title>
          <SignalCellularAltIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
          Monitor de Webhooks (API Oficial)
        </Title>
        <Box display="flex" alignItems="center">
          <Tooltip title="Atualizar">
            <IconButton onClick={handleRefresh} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={autoRefresh ? "Desativar atualização automática" : "Ativar atualização a cada 30s"}>
            <Button
              size="small"
              variant={autoRefresh ? "contained" : "outlined"}
              color="primary"
              className={classes.autoRefreshBtn}
              onClick={() => setAutoRefresh((v) => !v)}
            >
              {autoRefresh ? "Auto ON" : "Auto OFF"}
            </Button>
          </Tooltip>
        </Box>
      </MainHeader>

      <Paper className={classes.mainPaper} elevation={0}>
        {/* Seletor de período */}
        <Box className={classes.filterRow}>
          <FormControl className={classes.filterControl} size="small" variant="outlined">
            <InputLabel>Período</InputLabel>
            <Select
              value={period}
              onChange={(e) => { setPeriod(e.target.value); setPage(0); }}
              label="Período"
            >
              {PERIOD_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl className={classes.filterControl} size="small" variant="outlined">
            <InputLabel>Provedor</InputLabel>
            <Select
              value={filterProvider}
              onChange={(e) => { setFilterProvider(e.target.value); setPage(0); }}
              label="Provedor"
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="META">META</MenuItem>
              <MenuItem value="GUPSHUP">GUPSHUP</MenuItem>
              <MenuItem value="UNKNOWN">UNKNOWN</MenuItem>
            </Select>
          </FormControl>

          <FormControl className={classes.filterControl} size="small" variant="outlined">
            <InputLabel>Direção</InputLabel>
            <Select
              value={filterDirection}
              onChange={(e) => { setFilterDirection(e.target.value); setPage(0); }}
              label="Direção"
            >
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="INBOUND">INBOUND</MenuItem>
              <MenuItem value="STATUS">STATUS</MenuItem>
              <MenuItem value="VERIFICATION">VERIFICATION</MenuItem>
            </Select>
          </FormControl>

          <FormControl className={classes.filterControl} size="small" variant="outlined">
            <InputLabel>Status</InputLabel>
            <Select
              value={filterSuccess}
              onChange={(e) => { setFilterSuccess(e.target.value); setPage(0); }}
              label="Status"
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="true">Sucesso</MenuItem>
              <MenuItem value="false">Falha</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            variant="outlined"
            label="Tipo de evento"
            value={filterEventType}
            onChange={(e) => { setFilterEventType(e.target.value); setPage(0); }}
            style={{ minWidth: 160 }}
          />
          <TextField
            size="small"
            variant="outlined"
            label="Telefone (De/Para)"
            value={filterPhone}
            onChange={(e) => { setFilterPhone(e.target.value); setPage(0); }}
            style={{ minWidth: 170 }}
            placeholder="Ex: 5519971366740"
          />
        </Box>

        {/* Cards de resumo */}
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Card elevation={1}>
              <CardContent className={classes.statCard}>
                {statsLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    <Typography className={`${classes.statValue} ${classes.primaryColor}`}>
                      {stats?.total ?? "-"}
                    </Typography>
                    <Typography className={classes.statLabel}>Total recebidos</Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card elevation={1}>
              <CardContent className={classes.statCard}>
                {statsLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    <Typography className={`${classes.statValue} ${classes.successColor}`}>
                      {stats?.successes ?? "-"}
                    </Typography>
                    <Typography className={classes.statLabel}>Processados com sucesso</Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card elevation={1}>
              <CardContent className={classes.statCard}>
                {statsLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    <Typography className={`${classes.statValue} ${classes.errorColor}`}>
                      {stats?.failures ?? "-"}
                    </Typography>
                    <Typography className={classes.statLabel}>Falhas</Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card elevation={1}>
              <CardContent className={classes.statCard}>
                {statsLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    <Typography
                      className={`${classes.statValue} ${
                        parseFloat(stats?.successRate) >= 95
                          ? classes.successColor
                          : parseFloat(stats?.successRate) >= 80
                          ? classes.warningColor
                          : classes.errorColor
                      }`}
                    >
                      {stats?.successRate ?? "-"}%
                    </Typography>
                    <Typography className={classes.statLabel}>Taxa de sucesso</Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Gráfico de volume por hora */}
        {chartData.length > 0 && (
          <Paper variant="outlined" className={classes.chartSection}>
            <Typography variant="subtitle1" gutterBottom>
              Volume por hora
            </Typography>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hora" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="Sucesso" fill="#4caf50" stackId="a" />
                <Bar dataKey="Falhas" fill="#f44336" stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        )}

        {/* Erros recentes */}
        {stats?.recentErrors?.length > 0 && (
          <Paper variant="outlined" style={{ padding: 12, marginBottom: 16 }}>
            <Typography variant="subtitle1" style={{ color: "#d32f2f", marginBottom: 8 }}>
              <ErrorIcon style={{ verticalAlign: "middle", marginRight: 4, fontSize: 18 }} />
              Últimas falhas
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Horário</TableCell>
                  <TableCell>Provedor</TableCell>
                  <TableCell>Evento</TableCell>
                  <TableCell>Número</TableCell>
                  <TableCell>Erro</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.recentErrors.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell style={{ whiteSpace: "nowrap" }}>
                      {moment(r.createdAt).format("DD/MM HH:mm:ss")}
                    </TableCell>
                    <TableCell>{r.provider}</TableCell>
                    <TableCell>{r.eventType}</TableCell>
                    <TableCell>{r.fromNumber || "-"}</TableCell>
                    <TableCell style={{ color: "#d32f2f", maxWidth: 300 }}>
                      {r.errorCode ? `[${r.errorCode}] ` : ""}
                      {r.errorMessage || r.failureReason || "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}

        <Divider style={{ marginBottom: 12 }} />

        {/* Tabela de registros */}
        {loading ? (
          <Box display="flex" justifyContent="center" padding={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data/Hora</TableCell>
                  <TableCell>Provedor</TableCell>
                  <TableCell>Direção</TableCell>
                  <TableCell>Evento</TableCell>
                  <TableCell>De</TableCell>
                  <TableCell>Para</TableCell>
                  <TableCell>Status Msg</TableCell>
                  <TableCell>Tempo (ms)</TableCell>
                  <TableCell align="center">Resultado</TableCell>
                  <TableCell align="center">Detalhe</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography variant="body2" color="textSecondary">
                        Nenhum registro encontrado
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  records.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell style={{ whiteSpace: "nowrap" }}>
                        {moment(r.createdAt).format("DD/MM HH:mm:ss")}
                      </TableCell>
                      <TableCell>
                        <Chip label={r.provider} size="small" style={{ fontSize: "0.7rem" }} />
                      </TableCell>
                      <TableCell>{r.direction}</TableCell>
                      <TableCell style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.eventType}
                      </TableCell>
                      <TableCell>{r.fromNumber || "-"}</TableCell>
                      <TableCell>{r.toNumber || "-"}</TableCell>
                      <TableCell>{r.status || "-"}</TableCell>
                      <TableCell>{r.processingMs != null ? `${r.processingMs}ms` : "-"}</TableCell>
                      <TableCell align="center">
                        {r.success ? (
                          <CheckCircleIcon style={{ color: "#4caf50", fontSize: 18 }} />
                        ) : (
                          <Tooltip title={r.errorMessage || r.failureReason || "Falha"}>
                            <ErrorIcon style={{ color: "#f44336", fontSize: 18 }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver payload">
                          <IconButton size="small" onClick={() => handleOpenDetail(r)}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            />
          </TableContainer>
        )}
      </Paper>

      {/* Dialog de detalhe */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalhe do Webhook
          {selectedRecord && (
            <Typography variant="subtitle2" color="textSecondary">
              #{selectedRecord.id} — {moment(selectedRecord.createdAt).format("DD/MM/YYYY HH:mm:ss")} — {selectedRecord.provider} / {selectedRecord.eventType}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedRecord && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">De</Typography>
                <Typography variant="body2">{selectedRecord.fromNumber || "-"}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Para</Typography>
                <Typography variant="body2">{selectedRecord.toNumber || "-"}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">messageId</Typography>
                <Typography variant="body2" style={{ wordBreak: "break-all" }}>
                  {selectedRecord.messageId || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="textSecondary">Status</Typography>
                <Typography variant="body2">{selectedRecord.status || "-"}</Typography>
              </Grid>
              {!selectedRecord.success && (
                <>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="textSecondary">Código de erro</Typography>
                    <Typography variant="body2" style={{ color: "#d32f2f" }}>
                      {selectedRecord.errorCode || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <Typography variant="caption" color="textSecondary">Mensagem de erro</Typography>
                    <Typography variant="body2" style={{ color: "#d32f2f" }}>
                      {selectedRecord.errorMessage || selectedRecord.failureReason || "-"}
                    </Typography>
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">Payload</Typography>
                <Box className={classes.payloadBox}>
                  {JSON.stringify(selectedRecord.payload, null, 2)}
                </Box>
              </Grid>
              {selectedRecord.rawPayload && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Raw Payload</Typography>
                  <Box className={classes.payloadBox}>
                    {JSON.stringify(selectedRecord.rawPayload, null, 2)}
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)} color="primary">Fechar</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default WebhookMonitor;
